import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock } from "lucide-react";
import { useConsent, type ConsentTier } from "@/contexts/consent-context";
import { Link } from "wouter";

interface TierConfig {
  tier: ConsentTier;
  label: string;
  description: string;
}

const TIERS: TierConfig[] = [
  {
    tier: "analytics",
    label: "Analytics",
    description: "Helps us understand how visitors use the site so we can improve content and navigation. No personal data is shared with advertisers.",
  },
  {
    tier: "marketing",
    label: "Marketing",
    description: "Allows us to show you relevant content and measure campaign effectiveness on other platforms (e.g. LinkedIn, Meta).",
  },
];

interface Props {
  onClose: () => void;
}

export function CookiePreferencesModal({ onClose }: Props) {
  const { consent, setConsent } = useConsent();
  const [local, setLocal] = useState({ analytics: consent.analytics, marketing: consent.marketing });

  const handleSave = () => {
    (Object.keys(local) as ConsentTier[]).forEach(tier => setConsent(tier, local[tier]));
    onClose();
  };

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="modal-cookie-preferences">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Choose which cookies you allow. Strictly necessary cookies cannot be disabled.{" "}
            <Link href="/page/cookie-policy" className="text-primary underline hover:no-underline">
              Cookie Policy
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Strictly necessary — locked */}
          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-medium">Strictly Necessary</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  <Lock className="h-2.5 w-2.5 mr-0.5" />Always on
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Required for the site to function — session management, security, and preferences. Cannot be disabled.
              </p>
            </div>
            <Switch checked disabled aria-label="Strictly necessary cookies always on" data-testid="switch-strictly-necessary" />
          </div>

          {/* Configurable tiers */}
          {TIERS.map(({ tier, label, description }) => (
            <div key={tier} className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-0.5">{label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
              </div>
              <Switch
                checked={local[tier]}
                onCheckedChange={v => setLocal(prev => ({ ...prev, [tier]: v }))}
                aria-label={`${label} cookies`}
                data-testid={`switch-consent-${tier}`}
              />
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="button-cancel-prefs">
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} data-testid="button-save-cookie-prefs">
            Save preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
